import { keyframes, styled } from "goober";
import { useEffect, useId, useState } from "react";
import { CircleMesh } from "./CircleMesh";
import { CircleMeshEditor, parseBadgeConfig } from "./CircleMeshEditor";

const jiggle = keyframes`
  0%, 100% { transform: rotate(-3deg) scale(1.08); }
  50% { transform: rotate(3deg) scale(1.08); }
`;

const BadgeContainer = styled("div")`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
`;

const blueprintOverlayStyle: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  pointerEvents: "none",
  overflow: "visible",
};

function BlueprintOverlay() {
  return (
    <svg
      style={blueprintOverlayStyle}
      viewBox="0 0 2 2"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="0"
        y="0"
        width="2"
        height="2"
        rx="0.5"
        ry="0.5"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.03125"
      />
      <line
        x1="0.5"
        y1="-0.1875"
        x2="0.5"
        y2="2.1875"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.03125"
      />
      <line
        x1="1.5"
        y1="-0.1875"
        x2="1.5"
        y2="2.1875"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.03125"
      />
      <line
        x1="-0.1875"
        y1="0.5"
        x2="2.1875"
        y2="0.5"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.03125"
      />
      <line
        x1="-0.1875"
        y1="1.5"
        x2="2.1875"
        y2="1.5"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.03125"
      />
      <circle
        cx="0.5"
        cy="0.5"
        r="0.5"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.03125"
      />
      <circle
        cx="1.5"
        cy="0.5"
        r="0.5"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.03125"
      />
      <circle
        cx="0.5"
        cy="1.5"
        r="0.5"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.03125"
      />
      <circle
        cx="1.5"
        cy="1.5"
        r="0.5"
        stroke="var(--text-secondary)"
        strokeOpacity={1}
        strokeWidth="0.03125"
      />
    </svg>
  );
}

type Props = {
  workflow: {
    id: string;
    badgeSeed: string | null;
    pausedAt: number | null;
    branches: readonly { name: string }[] | undefined;
  };
  hover: boolean;
  blueprint?: boolean;
};

export function WorkflowBadge({ workflow, hover, blueprint }: Props) {
  const hasLiveBranch =
    !blueprint && (workflow.branches ?? []).some((b) => b.name === "main");
  const badgeConfig = parseBadgeConfig(workflow.badgeSeed, workflow.id);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [shouldJiggle, setShouldJiggle] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [ignoreParentHover, setIgnoreParentHover] = useState(false);
  const anchorName = `--badge-${useId().replace(/:/g, "")}`;

  useEffect(() => {
    if (!isHovering || isEditorOpen) return;

    const handleKeyChange = (e: KeyboardEvent) => {
      setShouldJiggle(e.altKey);
    };

    window.addEventListener("keydown", handleKeyChange);
    window.addEventListener("keyup", handleKeyChange);
    return () => {
      window.removeEventListener("keydown", handleKeyChange);
      window.removeEventListener("keyup", handleKeyChange);
    };
  }, [isHovering, isEditorOpen]);

  const handleAltClick = (e: React.MouseEvent) => {
    if (e.altKey) {
      e.stopPropagation();
      e.preventDefault();
      e.nativeEvent.stopImmediatePropagation();
      setIsEditorOpen(true);
      setShouldJiggle(false);
    }
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    setIgnoreParentHover(false);
    setIsHovering(true);
    setShouldJiggle(e.altKey && !isEditorOpen);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    setShouldJiggle(false);
  };

  const handleEditorClose = () => {
    setIsEditorOpen(false);
    setIgnoreParentHover(true);
  };

  const effectiveHover = ignoreParentHover ? isHovering : hover;

  return (
    <div style={{ flexShrink: 0 }}>
      <div
        onMouseDownCapture={handleAltClick}
        onClickCapture={handleAltClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={
          {
            width: 32,
            height: 32,
            flexShrink: 0,
            position: "relative",
            transform: shouldJiggle
              ? undefined
              : effectiveHover
                ? "scale(1.08)"
                : "scale(1)",
            transition: shouldJiggle ? "none" : "transform 0.1s ease-out",
            animation: shouldJiggle
              ? `${jiggle} 0.15s ease-in-out infinite`
              : "none",
            anchorName,
          } as React.CSSProperties
        }
      >
        <BadgeContainer style={hasLiveBranch ? undefined : { opacity: 0.5 }}>
          <CircleMesh
            key={workflow.badgeSeed}
            targetCircleSize={16}
            seed={badgeConfig.seed}
            mutingIntensity={0}
            animate={false}
            restrictedColors={badgeConfig.colors}
            grayscale={workflow.pausedAt != null}
          />
        </BadgeContainer>
        {!hasLiveBranch && <BlueprintOverlay />}
      </div>
      <CircleMeshEditor
        workflowId={workflow.id}
        currentBadgeSeed={workflow.badgeSeed}
        isOpen={isEditorOpen}
        onClose={handleEditorClose}
        anchorName={anchorName}
      />
    </div>
  );
}
